import React, { useState } from "react";
import { Outlet } from "react-router-dom";
import Sidebar from "./Sidebar";
import Breadcrumbs from "./Breadcrumbs";
import ModuleAccessGate from "./ModuleAccessGate";
import NewRestaurantOrderPopup from "./NewRestaurantOrderPopup";
import NewRoomServiceRequestPopup from "./NewRoomServiceRequestPopup";
import HousekeepingAlertPopup from "./HousekeepingAlertPopup";
import CheckoutDuePopup from "./CheckoutDuePopup";
import {
  clearViewingTenant,
  getViewingTenant,
} from "../utils/partnerViewContext";
import { getAuthToken } from "../utils/apiClient";
import { getCurrentUser } from "../utils/authApi";
import { identityFromCurrentUser } from "../utils/jwtIdentity";
import { useRestaurantNewOrders } from "../hooks/useRestaurantNewOrders";
import { useRoomServiceRequestAlerts } from "../hooks/useRoomServiceRequestAlerts";
import { useTaxDisplayMode } from "../hooks/useTaxDisplayMode";
import { useHousekeepingAlerts } from "../hooks/useHousekeepingAlerts";
import { useCheckoutDueAlerts } from "../hooks/useCheckoutDueAlerts";

const AdminLayout: React.FC = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [viewingTenant] = useState(() => getViewingTenant());
  // Loads the account's "tax display on bill" setting for every screen that prints a bill.
  useTaxDisplayMode();
  const { alerts: housekeepingAlerts, acknowledgeAll: acknowledgeHousekeeping } =
    useHousekeepingAlerts();
  const { alerts: checkoutAlerts, acknowledgeAll: acknowledgeCheckout } =
    useCheckoutDueAlerts();
  const { popupOrder, buildingName, dismissPopup, acknowledgePopup } =
    useRestaurantNewOrders();
  const {
    popupRequest,
    buildingName: requestBuildingName,
    dismissPopup: dismissRequestPopup,
    acknowledgePopup: acknowledgeRequestPopup,
  } = useRoomServiceRequestAlerts();

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  const exitPartnerView = async () => {
    clearViewingTenant();
    // The SuperAdmin's own bearer token was never replaced while viewing the
    // tenant (only the cached `user` object was, to satisfy /admin's guard)
    // — re-resolve their real profile from that still-valid token via
    // /auth/me instead of wiping `user` entirely, or the Control Center's
    // own auth guard would reject them.
    const token = getAuthToken();
    if (token) {
      try {
        const currentUser = await getCurrentUser();
        localStorage.setItem(
          "user",
          JSON.stringify(identityFromCurrentUser(currentUser))
        );
      } catch {
        localStorage.removeItem("user");
      }
    } else {
      localStorage.removeItem("user");
    }
    window.location.href = "/control-center";
  };

  return (
    <div className="min-h-screen bg-appbg">
      <Sidebar isCollapsed={isSidebarCollapsed} onToggle={toggleSidebar} />

      <div
        className={`flex flex-col min-h-screen transition-all duration-300 ${
          isSidebarCollapsed ? "ml-16" : "ml-56"
        }`}
      >
        {viewingTenant && (
          <div className="bg-gradient-to-r from-navy-800 to-primary-600 text-white px-4 py-2 flex items-center justify-between text-sm flex-shrink-0">
            <span>
              Viewing <strong>{viewingTenant.tenantName}</strong>'s dashboard
              as partner admin
            </span>
            <button
              onClick={exitPartnerView}
              className="px-3 py-1 text-xs font-medium bg-white text-primary-700 rounded-lg hover:bg-primary-50 transition-colors"
            >
              Exit to Control Center
            </button>
          </div>
        )}

        <main className="flex-1 overflow-x-hidden overflow-y-auto bg-appbg p-4">
          <Breadcrumbs />
          <ModuleAccessGate>
            {/* The page chunk loads here, with the sidebar already on screen. */}
            <React.Suspense fallback={<div className="flex items-center justify-center py-20 text-sm text-gray-500">Loading…</div>}>
              <Outlet />
            </React.Suspense>
          </ModuleAccessGate>
        </main>

        {/* Footer */}
        <footer className="bg-white border-t border-gray-100 py-2 px-4 flex-shrink-0">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-1 md:space-y-0">
            <div className="text-xs text-gray-500">
              © {new Date().getFullYear()} innPilots. All rights reserved.
            </div>
            <div className="text-xs text-gray-500">
              Powered by{" "}
              <span className="font-medium text-primary-600 hover:text-primary-700 transition-colors">
                UrbanSpaceLabs
              </span>
            </div>
          </div>
        </footer>
      </div>

      {housekeepingAlerts.length > 0 && (
        <HousekeepingAlertPopup
          alerts={housekeepingAlerts}
          onAcknowledge={acknowledgeHousekeeping}
        />
      )}

      {checkoutAlerts.length > 0 && housekeepingAlerts.length === 0 && (
        <CheckoutDuePopup alerts={checkoutAlerts} onAcknowledge={acknowledgeCheckout} />
      )}

      {popupOrder ? (
        <NewRestaurantOrderPopup
          order={popupOrder}
          buildingName={buildingName}
          onDismiss={dismissPopup}
          onView={() => acknowledgePopup(popupOrder.orderId)}
        />
      ) : (
        popupRequest && (
          <NewRoomServiceRequestPopup
            request={popupRequest}
            buildingName={requestBuildingName}
            onDismiss={dismissRequestPopup}
            onView={() => acknowledgeRequestPopup(popupRequest.requestId)}
          />
        )
      )}
    </div>
  );
};

export default AdminLayout;
